import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild, ViewChildren } from '@angular/core';
import { DialerService } from 'dialer';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TabDirective } from 'ngx-bootstrap/tabs/public_api';
import { asyncScheduler, firstValueFrom, from, Observable, Subscription } from 'rxjs';
import { debounceTime, first, map, skip } from 'rxjs/operators';
import { AppState, RoleMgr, ShowRequest } from '../core';
import { AppTab } from '../core/app-tab';
import { ColorScheme } from '../core/color-scheme';
import { CorrelationRecord, MapImageOverlay, MapRouteGPXOverlay, MiscLeafNode, PathTestGroupCriteria, PathTestMapPoint, PathTestPoint, PathTestPointSeq, ScoutPath, isAndroid, isIos, sortByNumericWaypointOrder } from '../core/models';
import { DriveTestDoc } from '../core/schema/drive-test-schema';
import { PathTestDoc } from '../core/schema/path-test-schema';
import { DriveTestService, PathTestService, PathWaypointService, PointService, ScoutPointInfo, ScoutPointService, UserService } from '../core/services';
import { ConfirmModalComponent } from '../shared/components/confirm-modal/confirm-modal.component';
import { TableViewContext, TableViewQueryCriteria } from '../shared/components/table-view/table-view-component-base';
import { PathTestEditComponent } from './path-test-edit/path-test-edit.component';
import { PathTestLogComponent } from './path-test-log/path-test-log.component';
import { PathTestPointListVisibleColumns } from './path-test-point/path-test-point-list/path-test-point-list.component';
import { PathTestPointSeqStatusEditorComponent } from './path-test-point/path-test-point-seq-status-editor/path-test-point-seq-status-editor.component';
import { PathTestPointComponent } from './path-test-point/path-test-point.component';
import { PathViewStore } from './path-test.store';
import { PathTestPlotterComponent } from './path-test-plotter/path-test-plotter.component';
import { CorrelationTableComponent, correlationDefaultStatGroups } from '../post-processing/correlation-table/correlation-table.component';
import { Mutex } from 'async-mutex';
import { ScoutTaskDoc } from '../core/schema/scout-task-schema';
import { PathKmlViewerModal } from '../path/path-kml-viewer/path-kml-viewer-modal/path-kml-viewer-modal.component';

class PathTestLastChange {
  testPoint: PathTestPoint
  seq: PathTestPointSeq
  userName$: Observable<string>
}

export type TestDocArgs = { editPoint?: boolean } & (
  | { testDocId: string; pointTestId?: never }
  | { testDocId?: never; pointTestId: string }
);

const parseTestId = (id: string) => {
  if (id) {
    let testDocArgs: TestDocArgs;
    try {
      testDocArgs = JSON.parse(id);
    } catch (e: any) {
      testDocArgs = { testDocId: id };
    }
    if (
      (testDocArgs.testDocId && testDocArgs.pointTestId) ||
      (!testDocArgs.testDocId && !testDocArgs.pointTestId)
    ) {
      throw new Error(
        "Invalid TestDocArgs: Either 'testDocId' or 'pointTestId' must be set, but not both."
      );
    }
    return testDocArgs;
  }
  return null;
};


@Component({
  selector: 'app-path-test',
  templateUrl: './path-test.component.html',
  styleUrls: ['./path-test.component.scss'],
  providers: [PathViewStore]
})
export class PathTestComponent implements OnDestroy {

  @Input('pathTest') set inputPathTest( args: { id: string; doc: PathTestDoc}) {
    this.initTestDoc(args,this._pathTestService);
  }

  @Input('driveTest') set inputDriveTest( args: { id: string; doc: DriveTestDoc}) {
    this.initTestDoc(args,this._driveTestService);
  }

  correlationStatGroups = correlationDefaultStatGroups.filter(g => ['byCarrier', 'byOs', 'byModel'].includes(g.id));
  bInitializing = false;
  initMutex = new Mutex();
  initTestDoc = async (args: { id: string; doc: PathTestDoc|DriveTestDoc}, testService: PathTestService|DriveTestService) => {
    console.log(`initTestDoc(${args.id}) doc:`, args.doc?.toJSON())
    this.bInitializing = true;
    const releaseInitMutex = await this.initMutex.acquire();
    try {
      let { id, doc } = args;
      const {testDocId=undefined,pointTestId=undefined,editPoint=undefined} = id ? parseTestId(id) : {};
      if ( testDocId ) {
        if ( testDocId != this.pathTest?._id ) {
          doc = await testService.getById(testDocId);
        }
      } else if ( pointTestId ) {
        if ( this.pathTest?.testPoints?.find(tp => tp.id == pointTestId) ) {
          // findByTestPointId can be expensive so first just check active pathTest
          doc = this.pathTest;
        } else {
          doc = await testService.findOneByTestPointId(pointTestId);
        }
      }
      if ( doc != this.pathTest ) {
        this.unsubscribe();
        this.pathTest = doc;
        this.notes =  doc?.schedule?.scheduleNotes;
        this.pathTestService = testService;
        this.correlationContext = JSON.stringify(this.buildCorrelationContext());
        this.store.setPathTestDoc( doc );
        this.initTestPointsSubscription(doc);
      }
      if ( pointTestId && editPoint ) {
        this.showingPointComponent = true;
      }
      // When point changes we update browser URL but that causes duplicate initTestDoc() calls
      // prevent duplicate changes to this.store (issues with too many events)
      const currentTestPoint = await firstValueFrom(this.store.testPoint$);
      if ( currentTestPoint?.id != pointTestId ) {
        this.store.changeTestPointById(pointTestId);
      }
      this.store.changeTestPointById(pointTestId);
    } catch ( e: any ) {
      console.error('Failed to set path test',args, e);
      this.appState.notifyError(`Failed to set path test. ${e.message||e}`, e, 5);
    } finally {
      releaseInitMutex();
      this.bInitializing = false;
    }
  }

  pathTest : PathTestDoc | DriveTestDoc
  overlaySub : Subscription
  routeOverlaySub : Subscription

  isNotesSaveButtonShown: boolean = false ; // hidden by default

  notesFieldDisabled: boolean = true ;

  notesFieldDirty: boolean = false;

  @Input() set tabConfig( tabConf : AppTab ) {
    this.initTabRemovalBehavior(tabConf)
    tabConf.setSelected = this.onTabSelected
    tabConf.hasUnsavedData = this.hasUnsavedData
    this.closeTab = tabConf.close
  }

  closeTab : () => void

  uiLocked = false //TODO

  @ViewChild('statusEditor') statusEditor: PathTestPointSeqStatusEditorComponent
  @ViewChild('pathTestPointComponent') pathTestPointComponent: PathTestPointComponent
  @ViewChild('pathTestLog') pathTestLogComponent: PathTestLogComponent
  @ViewChild('pathTestPlotter') pathTestPlotter: PathTestPlotterComponent;
  @ViewChild("correlationTable") correlationTable: CorrelationTableComponent;
  @ViewChild('leftDiv') set leftDiv(value: ElementRef<HTMLDivElement>) {
    if (this.resizeObserverSubscription) {
      this.resizeObserverSubscription.unsubscribe();
    }
    if (value) {
      const resizeObservable = new Observable<ResizeObserverEntry[]>(subscriber => {
        const observer = new ResizeObserver(entries => {
          subscriber.next(entries);
        });
        observer.observe(value.nativeElement);
        return () => observer.disconnect();
      });

      this.resizeObserverSubscription = resizeObservable.pipe(
        debounceTime(1200)
      ).subscribe(entries => {
        this.ngZone.run(() => {
          this.pathTestPlotter?.refresh();
        });
      });
    }
  }
  private resizeObserverSubscription: Subscription;
  
  @Output('contextJsonChange') contextChangeEmitter = new EventEmitter<string>();

  pathTestOverlay : MapImageOverlay  
  pathTestGpx : MapRouteGPXOverlay
  
  // scout info available if user expands "Scout Information"
  scoutTask: ScoutTaskDoc;
  scoutPath: ScoutPath;
  fallbackToScoutOverlay: boolean = true;

  showingPointComponent : boolean = false
  pointColors = ColorScheme.PointColors

  overlayLoading = false
  pointsQuerySubscription: Subscription
  pathWaypointsQuerySubscription: Subscription
  testPointsSubscription: Subscription

  bLoadingWaypoints = false
  bLoadingPoints = false

  lastChange: PathTestLastChange = null

  notes: string = null

  visibleColumns : PathTestPointListVisibleColumns = {
    scoutPtInfo: false,
    callCounts: true,
    waypoint: true,
    seqStatus: true
  }

  constructor(
    protected pointService: PointService,
    protected _pathTestService: PathTestService,
    protected _driveTestService: DriveTestService,
    protected pathWaypointService: PathWaypointService,
    protected scoutPointService: ScoutPointService,
    protected bsModalService: BsModalService,
    public userService: UserService,
    public roleMgr: RoleMgr,
    protected datePipe: DatePipe,
    public appState : AppState,
    public dialerService: DialerService,
    public store: PathViewStore,
    private modalService: BsModalService,
    private ngZone: NgZone,
  ) {
  }

  pathTestService: PathTestService | DriveTestService;
  scoutPointInfoByPtId: { [ptId:string]: ScoutPointInfo } = {};

  ngOnDestroy(): void {
    this.unsubscribe();
    this.resizeObserverSubscription?.unsubscribe();
  }

  unsubscribe() {
    this.overlaySub?.unsubscribe();
    this.overlaySub = null;
    this.routeOverlaySub?.unsubscribe();
    this.routeOverlaySub = null;
    this.testPointSubscription?.unsubscribe();
    this.testPointSubscription = null;
    this.testPointsSubscription?.unsubscribe()
    this.testPointsSubscription = null
    this.pointsQuerySubscription?.unsubscribe()
    this.pointsQuerySubscription = null
    this.pathWaypointsQuerySubscription?.unsubscribe()
    this.pathWaypointsQuerySubscription = null
    this.scoutPathsSubscription?.unsubscribe;
    this.scoutPathsSubscription = null;
  }

  initTestPointsSubscription(testDoc: PathTestDoc|DriveTestDoc) {
    console.log('initTestPointsSubscription()')
    this.unsubscribe();

    if ( !testDoc ) {
      return;
    }

    this.overlaySub = testDoc.get$('overlay').subscribe( (overlay : MapImageOverlay) => {
      this.pathTestOverlay = overlay
    })
    this.routeOverlaySub = testDoc.get$('gpx').subscribe( (gpx : MapRouteGPXOverlay) => {
      this.pathTestGpx = gpx;
    })

    const initWaypointQueries = () => {
      this.bLoadingWaypoints = true
      const pathWaypointsQuery = this.pathWaypointService.rxCollection.find().where('pathId').eq(testDoc.pathId)
      this.pathWaypointsQuerySubscription?.unsubscribe()
      this.pathWaypointsQuerySubscription = pathWaypointsQuery.$.subscribe( pathWaypointDocs => {
        pathWaypointDocs.sort(sortByNumericWaypointOrder)
        this.bLoadingWaypoints = false
        this.bLoadingPoints = true
        let pointsQuery = this.pointService.rxCollection.findByIds$(pathWaypointDocs.map(wp=>wp.pointId))
        this.pointsQuerySubscription?.unsubscribe()
        this.pointsQuerySubscription = pointsQuery.subscribe( pointDocMap => {
          this.store.buildMapData({pathWaypointDocs,pointDocMap})
          this.bLoadingPoints = false
        })
      })
    }
    const initScoutInfoByPtId = async () => {
      this.scoutPathsSubscription?.unsubscribe();
      const scoutInfoByPtId: { [ptId:string]: ScoutPointInfo } = {};
      this.initializingScoutInfoByPtId = true;
      try {
        const infoArray = await this.scoutPointService.buildScoutInfoForBuildingAndPath({
          buildingId: testDoc.buildingId,
          pathId: testDoc.pathId,
          stage: testDoc.stage,
        });
        this.scoutPathsSubscription = infoArray[0]?.scout.get$('scoutPaths').pipe(skip(1)).subscribe( scoutPaths => {
          // TODO only reload if specific path changed
          asyncScheduler.schedule(()=>{
            this.initTestPointsSubscription(this.pathTest);
          })
        })
        infoArray.forEach( scoutPtInfo => {
          scoutInfoByPtId[scoutPtInfo.scoutPoint.pointId||scoutPtInfo.scoutPoint.exPointId] = scoutPtInfo;
        });
        this.scoutPointInfoByPtId = scoutInfoByPtId;
      } finally {
        this.initializingScoutInfoByPtId = false;
      }
    }

    this.testPointsSubscription = testDoc.get$('testPoints').subscribe( async testPoints => {
      this.store.changeTestPoints(testPoints)
      this.initLastChange(testPoints)
      if ( testDoc?.type == 'path test') {
        initWaypointQueries()
        initScoutInfoByPtId().catch(error=>this.pathTestService.publishError(error,this));        
      }
    });
    this.testPointSubscription = this.store.testPoint$.pipe().subscribe( testPoint => this.updateTestPointContext(testPoint) )
  }
  scoutPathsSubscription: Subscription;
  initializingScoutInfoByPtId = false;

  updateTestPointContext = ( testPoint: PathTestPoint ) => {
    if ( !testPoint?.id && !this.pathTest?._id ) {
      return;
    }
    const testArgs: TestDocArgs = testPoint?.id ? {pointTestId: testPoint.id} : {testDocId: this.pathTest._id};
    testArgs.editPoint = testPoint && this.showingPointComponent;
    this.contextChangeEmitter.emit( JSON.stringify(testArgs));
  }

  testPointSubscription : Subscription;

  initLastChange( testPoints: PathTestPoint[]) {
    this.lastChange = null
    const [testPoint,seq] = this.pathTestService.findLatestTestPointAndSeq(testPoints)
    if ( testPoint ) {
      let userName$ : Observable<string> = null
      if ( seq?.userId ) {
        userName$ = from(this.userService.getById(seq.userId))
          .pipe( map( userDoc=> userDoc ? ` by ${userDoc.firstName} ${userDoc.lastName}` : ''))
      }
      this.lastChange = {
        testPoint,
        seq,
        userName$
      }
    }
  }


  async onExportPath() {
    try {
      await this.pathTestService.export(this.pathTest)
    } catch( error: any ) {
      console.error(error)
      const msg = this.pathTestService.extractErrorMsg(error)
      this.appState.notifyError(msg,this,30)
      this.pathTestService.publishError(error,this)
    }
  }

  onShowDialerCallLogs() {
    const seqIds = this.pathTest.testPoints.flatMap( tp => tp.seqs.last()?.dialerSeqId );
    const queryForSeqId : TableViewQueryCriteria = {
      filters: [ {
        key: 'seqid',
        value: `^(${seqIds.join('|')})$`,
        mode: '~*'
      }],
      sorts: [
        {
          key: 'starttime',
          order: 'DESC'
        }
      ]
    };
    this.appState.showView.next(
      new ShowRequest('tab', 'dialer call logs', JSON.stringify(queryForSeqId))
    );
  }

  showTaskCalls() {
    const queryForTaskCalls : Partial<TableViewContext> = {
      filters: [ {
        key: 'buildingid',
        value: this.pathTest.buildingId,
        mode: '='
      },
      {
        key: 'testtype',
        value: 'dialer',
        mode: '='
      },
      {
        key: 'stage',
        value: this.pathTest.stage,
        mode: '='
      },
      {
        key: 'pathid',
        value: this.pathTest.pathId,
        mode: '='
      },
      {
        key: 'cycle',
        value: `${this.pathTest.cycle}`,
        mode: '='
      }],
      sorts: [
        {
          key: 'starttime',
          order: 'DESC'
        }
      ],
      visibleNames: ['sourceid','starttime', 'endtime', 'duration', 'setup', 'traffic', 'testtype', 'taskid', 'testpoint', 'stage', 'cycle', 'pathid', 'pointid', 'buildingid', 'carrier', 'username' ]
    };
    this.appState.showView.next(
      new ShowRequest('tab', 'call logs', JSON.stringify(queryForTaskCalls))
    );
  }
  
  onShowTestingSummary() {
    const groupCriteria: PathTestGroupCriteria = { stage: this.pathTest.stage, buildingId: this.pathTest.buildingId }
    const strGroupFilter = JSON.stringify( groupCriteria )
    this.appState.showView.next( new ShowRequest('tab', 'path test group', strGroupFilter, null) )
  }

  async onOpenPathEditDlg() {
    const initialState = {
      pathTestDoc: this.pathTest,
      doAfterSaveFn: () => {
        this.pathTestPlotter?.refresh();
      }
    }
    const modalRef = this.bsModalService.show(PathTestEditComponent, {ignoreBackdropClick: true, initialState});
    const component: PathTestEditComponent = modalRef.content
    const sub = component.deleted$.subscribe( doc => {
      this.appState.notifySuccess('Path test deleted for path ' + doc.pathId,this,3)
      //TODO - delete comment docs, test seqs, etc...????
      modalRef.hide()
      this.closeTab && this.closeTab()
    })
    await modalRef.onHidden.pipe(first()).toPromise()
    sub.unsubscribe()
  }

  async onDeletePathTest() {
    await this.pathTestService.deletePathTestModal(this.bsModalService,this.pathTest)
  }

  setSelectedMapPoint(mapPoint: PathTestMapPoint ) {
    console.log(`setSelectedMapPoint(${mapPoint?.name})`)
    this.store.changeTestPointById(mapPoint.pathTestPoint?.id)
  }

  onEditPointTest = async (testPoint : PathTestPoint) => {
    this.store.changeTestPointByPointId(testPoint.pointId)
    this.showingPointComponent=true
    this.updateTestPointContext(testPoint);
  }

  onEditPointTestDrive = async (testPoint : PathTestPoint) => {
    this.store.changeTestPointByOrder(testPoint.order)
    this.showingPointComponent=true
    this.updateTestPointContext(testPoint);
  }

  onChangeSelectedTestPoint( testPoint : PathTestPoint ) {
    this.store.changeTestPointById(testPoint?.id);
    this.updateTestPointContext(testPoint);
  }

  // map point selected in plotter
  onPlotterPointSelected( mapPoint : PathTestMapPoint ) {
    console.log('onMapPointSelected: ' + mapPoint?.id)
    this.store.changeTestPointById( mapPoint?.pathTestPoint?.id )
  }

  onOverlayEdited( overlay : MapImageOverlay  ) {
    this.pathTestOverlay = overlay as MapImageOverlay
  }

  onGpxEdited( gpx : MapRouteGPXOverlay ) {
    this.pathTestGpx = gpx as MapRouteGPXOverlay
  }


  onResolvedScoutPath(evt: {scoutTask: ScoutTaskDoc, scoutPath: ScoutPath}) {
    this.scoutTask = evt.scoutTask;
    this.scoutPath = evt.scoutPath;
  }

  setSelectedTestPointById( testPointId: string ) {
    console.log('setSelectedTestPointById: ' + testPointId)
    this.store.changeTestPointById(testPointId)
  }

  async onCloseEditor() {
    const testPoint = await this.store.testPoint$.pipe(first()).toPromise()
    this.changeSelectedPointTestGuard( testPoint, () => {
      this.showingPointComponent=false;
      this.updateTestPointContext(testPoint);
      //this.store.changeTestPoint(testPoint)
      //this.store.changeTestPointIndex(0)
    } )
  }


  isEditing = () => this.hasUnsavedData();

  changeSelectedMapPointTestGuard = (
    current: PathTestMapPoint,
    acceptFn: () => void
  ) => {
    this.changeSelectedPointTestGuard(current.pathTestPoint, acceptFn);
  };

  changeSelectedPointTestGuard = (
    current: PathTestPoint,
    acceptFn: () => void
  ) => {
    this.pathTestPointComponent.changeSelectedPointTestGuard(acceptFn);
  };

  initTabRemovalBehavior(tabConfig: any) {
    tabConfig.isEditing = this.isEditing;
    const defaultRemove = tabConfig.remove;
    tabConfig.remove = (te: TabDirective) => {
      if (!this.isEditing()) {
        defaultRemove(te);
      } else {
        this.askDiscardEdits(() => defaultRemove(te));
      }
    };
  }

  bTabActive = false;

  onTabSelected = (bSelected: boolean) => {
    console.log("path-test tab selected: " + bSelected);
    this.bTabActive = bSelected; // prepare to show path plotter
    if (bSelected) {
      this.initTestPointsSubscription(this.pathTest);
    } else {
      this.unsubscribe();
    }
  };

  hasUnsavedData = () => this.hasUnsavedNotes() || this.hasUnsavedComments() || this.hasUnsavedPathTestPointData()
      || this.hasLocalTestInProgress();

  hasUnsavedPathTestPointData = () => this.pathTestPointComponent?.isEditing();

  hasLocalTestInProgress = () => this.pathTestPointComponent?.isLocalTestInProgress();

  hasUnsavedNotes = () => !this.notesFieldDisabled && this.notes?.length > 0;

  hasUnsavedComments = () => this.pathTestLogComponent?.hasUnsavedComment();

  async askDiscardEdits(acceptDiscardFn: () => void) {
    let approvedDiscard : boolean = true;
    if ( this.hasUnsavedNotes() || this.hasUnsavedComments() ) {
      const unsavedNames = [this.hasUnsavedNotes()&&'Notes',this.hasUnsavedComments()&&'Comments'].filter(n=>n);
      let initialState = {
        onNo: () => approvedDiscard = false,
        title: 'Unsaved Path Test Data',
        question: `Discard ${unsavedNames.join(', ')}?`,
        focusOnNo: true,
        yesStyle: "btn btn-danger",
      };
      const modalRef = this.modalService.show(ConfirmModalComponent, { initialState });
      await firstValueFrom(modalRef.onHidden);
    }
    if ( approvedDiscard && (this.hasUnsavedPathTestPointData() || this.hasLocalTestInProgress() )) {
      approvedDiscard = false;
      await this.pathTestPointComponent.askDiscardEdits(()=>approvedDiscard=true);
    }
    if ( approvedDiscard ) {
      acceptDiscardFn();
    }

  }

  notesChangeFn(e) {

    let countDirtyValues = 0;
    Object.values(e).map(value => {
      if (value) {
        countDirtyValues += 1;
      }
    });
    if (countDirtyValues >= 1) {
      this.notesFieldDirty = true;
    }

    this.notes = e;
  }

  async onSaveNotes(){

    if(this.pathTest) {
      await this.pathTest.atomicPatch({schedule : {scheduleNotes : this.notes}})
    }
    this.notesFieldDirty = false;
    this.isNotesSaveButtonShown = false;
    this.notesFieldDisabled = !this.notesFieldDisabled;
  }

  async onCancel() {

    if(this.notesFieldDirty == false){
      this.notesFieldDisabled = true;
      this.isNotesSaveButtonShown = false;
      return
    }

    let modalResponseAffirm : boolean = false;
    const initialState = {
      onYes: () => {
        modalResponseAffirm = true;
        this.notesFieldDirty = false;
      },
      onNo: () => {
        modalResponseAffirm = false;
      },
      question: "Discard notes field changes?",
      focusOnNo: true,
      yesStyle: "btn btn-danger",
    };
    let data = this.modalService.show(ConfirmModalComponent, {
      ignoreBackdropClick: true,
      initialState,
    });
    await data.onHidden
      .pipe(first())
      .toPromise()
      .then((data) => {
        if (modalResponseAffirm) {
          this.notesFieldDisabled = true;
          this.isNotesSaveButtonShown = false;
          this.notes = this.pathTest?.schedule.scheduleNotes;
        } else {
          this.isNotesSaveButtonShown = true;
          this.notesFieldDisabled = false;
        }
      });
  }
  editNotes() {
    if (this.roleMgr.canEditTasks) {
      this.notesFieldDisabled = !this.notesFieldDisabled;
      this.isNotesSaveButtonShown = true;
    }
  }


  correlationVisible = false;
  correlationContext: string;
  hideTestPointsTable = false;
  mapHeight = '550px';

  async onMapHeightChange(event: Event) {
    asyncScheduler.schedule( () => {
      this.pathTestPlotter?.refresh();
    });
  }

  toggleCorrelationVisible = () => {
    this.correlationVisible =!this.correlationVisible;
    if ( !this.correlationVisible ) {
      this.hideTestPointsTable = false;
    }

    if ( !this.correlationVisible) {
      const map = this.pathTestPlotter;
      if ( !map && !map.points) {
        console.warn('No map to plot correlation records');
        return;
      }
      map.points.forEach((mpt:PathTestMapPoint) => mpt.miscChildren = []);
      map.plotWhenReady(map.points);
    } else {
      // template will add table and trigger load
    }
  }

  onCorrelationRecordsLoaded = (rows: CorrelationRecord[]) => {
    const map = this.pathTestPlotter;
    if ( !map ) {
      console.warn('No map to plot correlation records');
      return;
    }
    const mapPoints = map?.points;
    mapPoints?.forEach((mpt:PathTestMapPoint) => {
      const ptCorrelationRecs = rows.filter(cr=>cr.pointid == mpt.pointId);
      mpt.miscChildren = [];
      const children: MiscLeafNode[] = mpt.miscChildren;
      ptCorrelationRecs.forEach(cr=>{
        if ( cr.latitude != null && cr.longitude != null ) {
          const child =  {
            latitude: Number(cr.latitude),
            longitude: Number(cr.longitude),
            altitudeEllipsoidMeters: Number(cr.altitude),
            id: cr.viewrowid,
            parentId: mpt.id,
            title: `${cr.pointid} | ${cr.horizontalerror?.toFixed(2)}m | ${cr.carrier} | ${cr.type} | ${cr.summarytech}`,
            type: 'call log',
            iconClass: isAndroid(cr.os) ? 'fab fa-android android-icon' : isIos(cr.os) ? 'fab fa-apple apple-icon' : undefined,
            data: cr
          };
          children.push(child);
        }
      });
    });
    map.plotWhenReady(mapPoints);
  }

  buildCorrelationContext(): Partial<TableViewContext> {
    const ctx: Partial<TableViewContext>  = {
      filters: [
        {
          key: 'pathid',
          value: this.pathTest.pathId,
          mode: '='
        },
        {
          key: 'stage',
          value: this.pathTest.stage,
          mode: '='
        },
        {
           key: 'cycle',
           value: `${this.pathTest.cycle}`,
           mode: '='
        },
        {
           key: 'correlatedcall',
           value: 'true',
           mode: '='
        }
      ],
      visibleNames:[
        'pointid', 'os', 'carrier', 'modelname', 'correlatedcall',
         'callstarttime','callduration','rantech','summarytech','type',
         'horizontalerror','verticalerror','locationtime','timetofix'
      ],
      sorts: [
        {
          key: 'horizontalerror',
          order: 'DESC'
        },
        {
          key: 'locationtime',
          order: 'DESC'
        }
      ]
    }
    return ctx;
  }

  onMiscLeafNodeEvent = (evt: {srcEvent: MouseEvent, id : number, parentId: string}) => {
    if ( !this.correlationTable ) {
      return;
    }
    const recIndex = this.correlationTable.records.findIndex(cr=>cr.viewrowid == evt.id);
    if ( recIndex === -1 ) {
      console.warn('onMiscLeafNodeEvent() no correlation record found for id', evt.id);
      return;
    }
    const rec = this.correlationTable.records[recIndex]
    if ( evt.srcEvent.type==='click' ) {
      this.correlationTable.showDetailModal(rec);
    } else if ( evt.srcEvent.type==='mouseover') {
      this.correlationTable.applySelectedIndices({
        selectedIndices:[recIndex],
        clearPreviousSelection:true,
        scrollIntoViewMode: false //TODO - only support scroll into view from a popup menu item
      });
    } else if (evt.srcEvent.type==='mouseout') {
      this.correlationTable.applySelectedIndices({
        selectedIndices:[],
        clearPreviousSelection:true,
      });
    }
  }

  correlationRowHover = (hoverEvt: { event:MouseEvent,record: CorrelationRecord}, active: boolean) => {
    //console.log('correlationRowHover()', hoverEvt,active);
    this.pathTestPlotter?.highlightLeafNode(hoverEvt.record.viewrowid, active);
  }

  async showKmlViewer() {
    try {
      const mapPoints = [...this.pathTestPlotter?.points];
      const scoutPtInfoByPtId = {...this.pathTestPlotter.scoutPtInfoByPtId};

      // correlationTable is not very performant so hide rows when showing cesium map
      let correlationTableVisible = true;
      if ( this.correlationTable ) {
        correlationTableVisible = this.correlationTable.isActiveView;
        this.correlationTable.isActiveView = false;
      }
      this.pathTestPlotter?.plotWhenReady([]);

      const buildingId = this.pathTest.buildingId;
      const paths = await this.pathWaypointService.findBuilding3dPaths(buildingId);
      const path = paths.find(p=>p.pathId == this.pathTest.pathId);
      if ( !path ) {
        console.warn('showKmlViewer() no path found for pathId', this.pathTest.pathId);
        this.appState.notifyError(`No path found for pathId "${this.pathTest.pathId}"`, this, 10);
        return;
      }
      for ( const mwpRow of path.mapWaypointRows ) {
        const mapPoint = mapPoints.find(p=>p.pointId == mwpRow.pointId);
        // console.log({mapPoint});
        if ( mapPoint) {
          const disabled = scoutPtInfoByPtId?.[mapPoint.pointId||mapPoint.exPointId]?.scoutPoint?.disabled ?? false;
          mwpRow.mapWaypoint = mapPoint;
          mwpRow.mapWaypoint.disabled = disabled;
        }
      }
      const overlaySource =
        this.pathTestOverlay ||
        (this.fallbackToScoutOverlay ? this.scoutPath?.overlay : undefined);
      const overlaysByPathId = overlaySource
        ? { [this.pathTest.pathId]: overlaySource }
        : {};
      // console.log('showKmlViewer()',{buildingId,paths});
      const modalRef = PathKmlViewerModal.show(this.modalService, {
        selectedPathIds: [this.pathTest.pathId],
        buildingId,
        paths,
        overlaysByPathId,
      });
      await firstValueFrom(modalRef.onHidden);
      if ( this.correlationTable ) {
        this.correlationTable.isActiveView = correlationTableVisible;
      }
      this.pathTestPlotter?.plotWhenReady(mapPoints);
    } catch(e:any){
      console.error('showKmlViewer() error', e);
      this.appState.notifyError("Issue preparing or rendering KML. " + e.message, this, 10);
    }
  }
}
